import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Row, Col, Card, CardBody, Container } from "reactstrap";
import moment from "moment";
import { Link } from "react-router-dom";
import { PopoverBody } from 'reactstrap';

import PopoverFormula from '../../../share-components/ui/PopoverFormula';
import BootstrapTable from "../../../share-components/table/BootstrapTable";
import DateRangePickerPrimary from "../../../share-components/ui/DateRangePickerPrimary";
import CommonDataTable from "../../../share-components/table/commonDataTable";
import Breadcrumb from "../../../layout/breadcrumb";

import withReducer from "../../../store/withReducer";
import reportReducer from "../index";

import {
    getInvoices,
    exportXeroInvoiceDetailReport,
} from "./store/vogaclosetReportSlice";

import {
    formatterCurrency
} from "../../../utils/common";


const VogaclosetInvoice = (props) => {

    const financesSummaryState = useSelector(
        ({ report }) => report.financesSummary
    );

    const vogaclosetReportState = useSelector(
        ({ report }) => report.vogaclosetReport
    );

    const dispatch = useDispatch();
    const queryParams = new URLSearchParams(window.location.search);

    // helper to ensure a value is a valid date/moment/string parseable by moment
    const isValidDate = (d) => d !== undefined && moment(d).isValid();

    // parse query params and normalize invalid dates to undefined
    const _rawEnd = queryParams.has('end_date') ? new Date(queryParams.get('end_date')) : undefined;
    const _rawStart = queryParams.has('start_date') ? new Date(queryParams.get('start_date')) : undefined;
    const endDateObj = isValidDate(_rawEnd) ? _rawEnd : undefined;
    const startDateObj = isValidDate(_rawStart) ? _rawStart : undefined;

    // initialize dateRange with validated dates (undefined when absent/invalid)
    const [dateRange, setDateRange] = useState([startDateObj, endDateObj]);
    const [startDate, endDate] = dateRange;

    // safe disabledDate: return false for falsy/invalid input
    const afterToday = () => {
        return (date) => {
            if (!isValidDate(date)) return false;
            return date.getTime() > Date.now();
        };
    };

    const [loading, setLoading] = useState(true);
    const [exportBtnDisabled, setExportBtnDisabled] = useState(false);
    const [timer, setTimer] = useState(null);

    // per-row export loading state: { [invoiceId]: boolean }
    const [exportLoadingMap, setExportLoadingMap] = useState({});

    const [lastFillters, setLastFillters] = useState(null);
    const [fillterCurrency, setFillterCurrency] = useState(null);
    const [filter, setFilter] = useState({
        page: 1,
        pageSize: 10,
        // set initial dates to null if not provided
        start_date: isValidDate(startDate) ? moment(startDate).format("YYYY-MM-DD") : null,
        end_date: isValidDate(endDate) ? moment(endDate).format("YYYY-MM-DD") : null,
    });

    const dataMenu = [
        {
            type: 0,
            text: "Reports",
        },
        {
            type: 0,
            text: "Partner Specific Invoices",
        },
    ];

    const getVogaclosetReport = () => {
        if (JSON.stringify(filter) == JSON.stringify(lastFillters)) return;

        setLastFillters({ ...filter });
        setLoading(true);

        dispatch(
            getInvoices(filter)
        ).then((res) => {
            if (!res.error) {
                setLoading(false);
            }
        });
    };

    useEffect(() => {
        getVogaclosetReport();
    }, [dispatch, filter]);


    const exportRow = async (row) => {
        if (!row || exportLoadingMap[row.id]) return;
        setExportLoadingMap(prev => ({ ...prev, [row.id]: true }));
        try {
            const params = { invoice_id: row.id }
            dispatch(exportXeroInvoiceDetailReport(params)).then((res) => {
                const link = document.createElement("a");
                link.href = res.payload;
                link.setAttribute("download", "");
                document.body.appendChild(link);
                link.click();
                link.remove();
            });
        } catch (err) {
            // handle error if needed
            console.error("Export failed for invoice", row.id, err);
        } finally {
            // keep button disabled briefly to show spinner; remove after short delay
            setTimeout(() => {
                setExportLoadingMap(prev => {
                    const copy = { ...prev };
                    delete copy[row.id];
                    return copy;
                });
            }, 1500);
        }
    };

    const tableColumns = [
        {
            name: 'Date',
            selector: row => row.invoice_date,
            left: true,
            grow: 1, // column size
        },
        {
            name: 'Invoice #',
            selector: row => row.invoice_number,
            left: true,
            grow: 0.5, // Larger column
        },
        {
            name: 'Type',
            selector: row => row.invoice_type || '-',
            left: true,
        },
        {
            name: 'Source Type',
            selector: row => row.source_type || '-',
            left: true,
        },
        {
            name: 'Xero Status',
            selector: row => {
                return <>
                    {row.xero_status || '-'}
                    {row.error_message && row.error_message != '' && (
                    <PopoverFormula className="ml-2 text-danger" id={'errorMessage_' + row.invoice_id}>
                        <PopoverBody>
                            <p>{row.error_message}</p>
                        </PopoverBody>
                    </PopoverFormula>
                    )}
                </>
            },
            left: true,
        },
        {
            name: 'Currency',
            selector: row => row.currency,
            left: true,
            grow: 0.5, // column size
        },
        {
            name: 'Total Gross Amount',
            selector: row => formatterCurrency(row.xero_total_gross_amount || 0),
            right: true,
        },
        {
            name: 'Total Tax',
            selector: row => formatterCurrency(row.xero_total_tax_amount || 0),
            right: true,
        },
        {
            name: 'Total Amount',
            selector: row => formatterCurrency(row.xero_total_net_amount || 0),
            right: true,
        },
        {
            name: 'Actions',
            selector: row => {
                const isLoading = !!exportLoadingMap[row.id];
                return (
                    <div className="text-right">
                        <Button
                            size="xs"
                            color="primary"
                            disabled={isLoading}
                            onClick={() => exportRow(row)}
                        >
                            {isLoading ? <i className="fa fa-spinner fa-spin mr-1"></i> : <i className="fa fa-download mr-1"></i>}
                            Export
                        </Button>
                    </div>
                );
            },
            right: true,
            grow: 0.4,
        },
    ];

    const onPageSizeChange = (newPageSize) => {
        setFilter(prev => ({
            ...prev,
            page: 1,
            pageSize: newPageSize === 'All' ? vogaclosetReportState.saleDetailsTableRow : newPageSize
        }));
    }

    const onPageChange = (e) => {
        setFilter({ ...filter, page: e })
    }

    useEffect(() => {
        setFilter(prev => ({
            ...prev,
            start_date: isValidDate(startDate) ? moment(startDate).format("YYYY-MM-DD") : null,
            end_date: isValidDate(endDate) ? moment(endDate).format("YYYY-MM-DD") : null,
        }));
    }, [startDate, endDate]);

    const customStyles = {
        control: (base) => ({
            ...base,
            minHeight: 50,
            border: "none",
        }),
    };

    return (
        <Fragment>
            <Breadcrumb title="Partner Specific Invoices" data={dataMenu} />
            <Container fluid={true} className="finances-summary-page feature-products">
                <Card className="px-3">
                    <Row>
                        <Col md="4">
                            <div className="p-3">
                                <DateRangePickerPrimary
                                    dateRange={dateRange}
                                    dateRangeChange={(date) => {
                                        setDateRange(date);
                                    }}
                                    // only provide defaultValue when we have validated dates
                                    defaultValue={(isValidDate(startDateObj) || isValidDate(endDateObj)) ? [startDateObj, endDateObj] : undefined}
                                    disabledDate={afterToday()}
                                />
                            </div>
                        </Col>
                    </Row>
                </Card>

                <Card className="px-3">
                    <CardBody>
                        <BootstrapTable
                            headerColumns={tableColumns}
                            gridData={vogaclosetReportState.invoiceLists}
                            noAction
                            progressPending={loading}
                            paginationServer
                            paginationTotalRows={vogaclosetReportState.invoiceTableRow}
                            currentPage={filter.page}
                            perPage={filter.pageSize}
                            onChangePage={onPageChange}
                            onChangeRowsPerPage={onPageSizeChange}
                            size="md"
                        />
                    </CardBody>
                </Card>
            </Container>
        </Fragment>
    );
}

export default withReducer([{ report: reportReducer }])(VogaclosetInvoice);